import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAuthenticatedApi } from './useAuthenticatedApi';
import MixpanelService from '../mixpanel';

// Types
interface Notification {
  id: string;
  user_id: string;
  title: string;
  message: string;
  read: boolean;
  type: 'credits' | 'system' | 'account' | 'security' | 'domain';
  metadata: Record<string, any>;
  workspace_id?: string;
  thread_id?: string;
  created_at: string;
  updated_at: string;
}

interface NotificationListResponse {
  notifications: Notification[];
  unread_count: number;
  total_count: number;
}

interface UseNotificationsOptions {
  pollingInterval?: number;
  pageSize?: number;
}

// Constants
const DEFAULT_POLLING_INTERVAL = 30000; // 30 seconds
const DEFAULT_PAGE_SIZE = 20;
const DELETE_CONFIRMATION_TIMEOUT = 2000;
const SCROLL_THRESHOLD = 50;

// Optimized date formatting with memoization
const formatDate = (dateString: string): string => {
  try {
    const utcDateString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    const date = new Date(utcDateString);
    
    if (isNaN(date.getTime())) {
      console.error('Invalid date string:', dateString);
      return '';
    }
    
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    // Show relative time for recent notifications
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    // Show formatted date for older notifications
    const options: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    };
    return date.toLocaleDateString('en-US', options);
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

export const useNotifications = (options: UseNotificationsOptions = {}) => {
  const {
    pollingInterval = DEFAULT_POLLING_INTERVAL,
    pageSize = DEFAULT_PAGE_SIZE
  } = options;

  // State
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastViewedCount, setLastViewedCount] = useState(0);
  const [hasViewedNotifications, setHasViewedNotifications] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [deleteConfirmations, setDeleteConfirmations] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  
  // Refs
  const dropdownRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const deleteTimeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const lastFetchTimeRef = useRef<number>(0);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);
  const didInitRef = useRef(false);
  
  const { user } = useAuth();
  const api = useAuthenticatedApi();

  // Set mounted state
  useEffect(() => {
    isMountedRef.current = true;
    
    const deleteTimeouts = deleteTimeoutsRef.current;

    return () => {
      isMountedRef.current = false;
      didInitRef.current = false; // Reset init ref only on unmount
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      // Clear all delete confirmation timeouts
      deleteTimeouts.forEach(timeout => clearTimeout(timeout));
      deleteTimeouts.clear();
    };
  }, []);

  // Abort any in-flight requests
  const abortPendingRequests = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  // Fetch notifications with better error handling and caching
  const fetchNotifications = useCallback(
    async (append = false, forceRefresh = false) => {
      if (!user || !isMountedRef.current) {
        return;
      }

      // Prevent rapid successive calls
      const now = Date.now();
      if (!forceRefresh && now - lastFetchTimeRef.current < 1000) {
        return;
      }
      lastFetchTimeRef.current = now;

      // Abort any pending requests
      abortPendingRequests();

      // Create new abort controller
      abortControllerRef.current = new AbortController();
      const { signal } = abortControllerRef.current;

      setError(null);
      // Show loading state appropriately
      if (!append) {
        setIsLoading(true);
      } else if (append) {
        setIsLoadingMore(true);
      }

      try {
        const skip = append ? notifications.length : 0;
        const params = new URLSearchParams({
          limit: pageSize.toString(),
          skip: skip.toString(),
          ...(showUnreadOnly && { unread_only: 'true' })
        });

        const response = await api.get<NotificationListResponse>(
          `/notifications/?${params}`,
          { signal }
        );

        if (!isMountedRef.current || signal.aborted) return;

        const newNotifications = response.notifications;
        
        // Update state atomically
        setNotifications(prev => {
          if (append) {
            // Deduplicate when appending
            const existingIds = new Set(prev.map(n => n.id));
            const uniqueNew = newNotifications.filter(n => !existingIds.has(n.id));
            return [...prev, ...uniqueNew];
          }
          return newNotifications;
        });

        setUnreadCount(response.unread_count);
        setTotalCount(response.total_count);
        setHasMore(skip + newNotifications.length < response.total_count);
        
      } catch (error: any) {
        if (error?.name === 'AbortError') return;
        
        console.error('Failed to fetch notifications:', error);
        if (!isMountedRef.current) return;
        
        setError('Failed to load notifications. Please try again.');
        if (!append) {
          setNotifications([]);
          setHasMore(false);
        }
      } finally {
        if (isMountedRef.current && !signal.aborted) {
          setIsLoading(false);
          setIsLoadingMore(false);
        }
      }
    },
    [user, notifications.length, showUnreadOnly, pageSize, abortPendingRequests, api]
  );


  // Mark as read/unread with optimistic updates
  const markAsRead = useCallback(async (notificationId: string) => {
    if (!api) return;

    const notification = notifications.find(n => n.id === notificationId);
    if (!notification || notification.read) return;

    // Optimistic update
    if (showUnreadOnly) {
      // Remove from list when in unread-only view
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } else {
      // Just mark as read in normal view
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
    }
    setUnreadCount(prev => Math.max(0, prev - 1));

    try {
      await api.post(`/notifications/${notificationId}/read`);
      MixpanelService.track('notification_marked_read', { notification_id: notificationId });
    } catch (error) {
      // Revert on error
      console.error('Failed to mark as read:', error);
      if (isMountedRef.current) {
        if (showUnreadOnly) {
          // Re-add to list if it was removed
          setNotifications(prev => [...prev, notification].sort((a, b) => 
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          ));
        } else {
          setNotifications(prev => prev.map(n => 
            n.id === notificationId ? { ...n, read: false } : n
          ));
        }
        setUnreadCount(prev => prev + 1);
      }
    }
  }, [api, notifications, showUnreadOnly]);

  const markAsUnread = useCallback(async (notificationId: string) => {
    if (!api) return;

    const notification = notifications.find(n => n.id === notificationId);
    if (!notification || !notification.read) return;

    // Optimistic update
    setNotifications(prev => prev.map(n => 
      n.id === notificationId ? { ...n, read: false } : n
    ));
    setUnreadCount(prev => prev + 1);

    try {
      await api.post(`/notifications/${notificationId}/unread`);
      MixpanelService.track('notification_marked_unread', { notification_id: notificationId });
    } catch (error) {
      // Revert on error
      console.error('Failed to mark as unread:', error);
      if (isMountedRef.current) {
        setNotifications(prev => prev.map(n => 
          n.id === notificationId ? { ...n, read: true } : n
        ));
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    }
  }, [api, notifications]);

  const markAllAsRead = useCallback(async () => {
    if (!api || unreadCount === 0) return;

    const previousState = {
      notifications: [...notifications],
      unreadCount
    };

    // Optimistic update
    if (showUnreadOnly) {
      // Clear all notifications when in unread-only view
      setNotifications([]);
    } else {
      // Mark all as read in normal view
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    }
    setUnreadCount(0);

    try {
      await api.post('/notifications/read-all');
      MixpanelService.track('all_notifications_marked_read', {
        previous_unread_count: previousState.unreadCount
      });
    } catch (error) {
      // Revert on error
      console.error('Failed to mark all as read:', error);
      if (isMountedRef.current) {
        setNotifications(previousState.notifications);
        setUnreadCount(previousState.unreadCount);
      }
    }
  }, [api, notifications, unreadCount, showUnreadOnly]);

  // Improved delete functionality
  const deleteNotification = useCallback(async (notificationId: string) => {
    if (!api) return;

    const notification = notifications.find(n => n.id === notificationId);
    if (!notification) return;

    // Store for potential revert
    const notificationIndex = notifications.findIndex(n => n.id === notificationId);
    
    // Optimistic update
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    setTotalCount(prev => Math.max(0, prev - 1));
    if (!notification.read) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }

    // Clear delete confirmation
    setDeleteConfirmations(prev => {
      const newSet = new Set(prev);
      newSet.delete(notificationId);
      return newSet;
    });

    try {
      await api.delete(`/notifications/${notificationId}`);
      MixpanelService.track('notification_deleted', { notification_id: notificationId });
    } catch (error) {
      // Revert on error
      console.error('Failed to delete notification:', error);
      if (isMountedRef.current) {
        setNotifications(prev => {
          const newNotifs = [...prev];
          newNotifs.splice(notificationIndex, 0, notification);
          return newNotifs;
        });
        setTotalCount(prev => prev + 1);
        if (!notification.read) {
          setUnreadCount(prev => prev + 1);
        }
      }
    }
  }, [api, notifications]);

  const handleDeleteClick = useCallback((notificationId: string) => {
    // Delete immediately without confirmation
    deleteNotification(notificationId);
  }, [deleteNotification]);

  // Handle notification click
  const handleNotificationClick = useCallback((notification: Notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }

    MixpanelService.track('notification_clicked', {
      notification_id: notification.id,
      notification_type: notification.type
    });

    closeDropdown();

    // Use React Router navigation if available, otherwise use window.location
    if (notification.thread_id && notification.workspace_id) {
      window.location.href = `/project/${notification.workspace_id}/thread/${notification.thread_id}`;
    } else if (notification.workspace_id) {
      window.location.href = `/project/${notification.workspace_id}`;
    }
  }, [markAsRead]);

  // Optimized scroll handler with debouncing
  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    if (isLoadingMore || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD) {
      fetchNotifications(true);
    }
  }, [isLoadingMore, hasMore, fetchNotifications]);

  // Toggle dropdown with tracking
  const handleToggleDropdown = useCallback(() => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);

    if (newIsOpen) {
      MixpanelService.track('notifications_dropdown_opened', {
        unread_count: unreadCount
      });
    } else {
      // When closing, mark that user has viewed notifications
      setHasViewedNotifications(true);
      setLastViewedCount(unreadCount);
    }
  }, [isOpen, unreadCount]);

  // Toggle unread filter
  const handleToggleUnreadOnly = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowUnreadOnly(prev => !prev);
  }, []);

  // Effect: Fetch initial notifications on mount
  useEffect(() => {
    if (user && api && !didInitRef.current && isMountedRef.current) {
      didInitRef.current = true;
      
      // Fetch initial notifications
      const doInitialFetch = async () => {
        try {
          setError(null);
          setIsLoading(true);
          
          const params = new URLSearchParams({
            limit: pageSize.toString(),
            skip: '0'
          });
          
          const response = await api.get<NotificationListResponse>(
            `/notifications/?${params}`
          );
          
          if (!isMountedRef.current) return;
          
          const newNotifications = response.notifications || [];
          setNotifications(newNotifications);
          setUnreadCount(response.unread_count || 0);
          setTotalCount(response.total_count || 0);
          setHasMore(newNotifications.length < (response.total_count || 0));
        } catch (error: any) {
          console.error('Failed to fetch notifications:', error);
          if (isMountedRef.current) {
            setError('Failed to load notifications. Please try again.');
            setNotifications([]);
            setHasMore(false);
          }
        } finally {
          if (isMountedRef.current) {
            setIsLoading(false);
          }
        }
      };
      
      doInitialFetch();
    }
  }, [user, api, pageSize]);

  // Effect: Polling for updates
  useEffect(() => {
    if (!user || !api) return;

    // Clear existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    // Set up polling - always poll to keep badge count updated
    pollingIntervalRef.current = setInterval(() => {
      fetchNotifications(false, true);
    }, pollingInterval);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [user, api, pollingInterval, fetchNotifications]);

  // Effect: Handle filter changes
  useEffect(() => {
    if (isOpen && user && api) {
      fetchNotifications(false, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showUnreadOnly]); // Only refetch when filter actually changes
  
  // Effect: Fetch fresh data when dropdown opens
  useEffect(() => {
    if (isOpen && user && api) {
      fetchNotifications(false, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // Only when dropdown opens/closes
  
  // Effect: Reset viewed state when all notifications are read
  useEffect(() => {
    if (unreadCount === 0 && hasViewedNotifications) {
      setHasViewedNotifications(false);
      setLastViewedCount(0);
    }
  }, [unreadCount, hasViewedNotifications]);

  // Function to close dropdown and track viewed state
  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    // Mark that user has viewed notifications
    setHasViewedNotifications(true);
    setLastViewedCount(unreadCount);
  }, [unreadCount]);

  // Effect: Click outside and escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const isDesktop = window.innerWidth >= 640;
      if (isDesktop && dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        const button = document.querySelector('[aria-label="Notifications"]');
        if (button && !button.contains(event.target as Node)) {
          closeDropdown();
        }
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, closeDropdown]);

  // Effect: Cleanup on dropdown close
  useEffect(() => {
    if (!isOpen) {
      // Abort pending requests
      abortPendingRequests();
      
      // Clear delete confirmations
      deleteTimeoutsRef.current.forEach(timeout => clearTimeout(timeout));
      deleteTimeoutsRef.current.clear();
      setDeleteConfirmations(new Set());
      
      // Clear error but keep filter preference
      setError(null);
    }
  }, [isOpen, abortPendingRequests]);

  // Memoized values
  const isInitialLoading = isLoading && notifications.length === 0;
  const hasNotifications = notifications.length > 0;
  
  // Calculate whether to show the number inside the badge
  // Show number if:
  // 1. User hasn't viewed notifications yet, OR
  // 2. New notifications arrived after viewing (count increased)
  const showBadgeNumber = !hasViewedNotifications || unreadCount > lastViewedCount;

  return {
    // State
    isOpen,
    notifications,
    unreadCount,
    showBadgeNumber,
    totalCount,
    isLoading: isInitialLoading || isLoading,
    isLoadingMore,
    hasMore,
    showUnreadOnly,
    deleteConfirmations,
    error,
    hasNotifications,
    
    // Refs
    dropdownRef,
    
    // Functions
    formatDate,
    handleToggleDropdown,
    handleScroll,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    handleDeleteClick,
    handleNotificationClick,
    handleToggleUnreadOnly,
    setIsOpen,
    closeDropdown,
    refresh: () => fetchNotifications(false, true),
    
    // User
    user
  };
};

export type { Notification, UseNotificationsOptions };